// lib/workspace-runtime-playback-state.ts
//
// Typed accessors for the timeline playback runtime that lives in the
// StudioWorkspace runtime overlay (kernel), so every graphical view of a
// workspace shares one source of truth. Mirrors the shape of
// `lib/project-runtime-layer-ui-state.ts`:
//   - get*  : imperative read via kernel.readEntityRuntimeState (stable, for callbacks)
//   - set*  : imperative write via kernel.patchEntityRuntimeState (transient, re-renders subscribers)
//   - select*: pure selector for `useKernelStore(state => ...)` reactive subscriptions
//
// The high-frequency cursor is intentionally NOT here — it keeps its throttled
// `setWorkspaceTimelineCursor` path (kernel/useWorkspaceTimeline.ts).
import type { KernelHandle, KernelState } from '#types/types';

export type RuntimeUpdater<T> = T | ((previous: T) => T);

const WORKSPACE_ENTITY = 'StudioWorkspace' as const;
const DEFAULT_PLAYBACK_RATE = 1;

const normalizeWorkspaceId = (workspaceId: string): string => workspaceId.trim();

const normalizeRate = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : DEFAULT_PLAYBACK_RATE;

const readRuntime = (kernel: KernelHandle, workspaceId: string) =>
  kernel.readEntityRuntimeState(WORKSPACE_ENTITY, normalizeWorkspaceId(workspaceId));

const resolveUpdater = <T>(updater: RuntimeUpdater<T>, previous: T): T =>
  typeof updater === 'function' ? (updater as (previous: T) => T)(previous) : updater;

// --- isTimelinePlaying ------------------------------------------------------

export const getWorkspaceIsTimelinePlaying = (
  kernel: KernelHandle,
  workspaceId: string
): boolean => readRuntime(kernel, workspaceId).isTimelinePlaying === true;

export const setWorkspaceIsTimelinePlaying = (
  kernel: KernelHandle,
  workspaceId: string,
  updater: RuntimeUpdater<boolean>
): void => {
  const normalizedId = normalizeWorkspaceId(workspaceId);
  if (!normalizedId) return;
  const next = Boolean(resolveUpdater(updater, getWorkspaceIsTimelinePlaying(kernel, normalizedId)));
  kernel.patchEntityRuntimeState(WORKSPACE_ENTITY, normalizedId, { isTimelinePlaying: next });
};

/** Stops those of the workspaces whose timeline is playing; one that is not gets no write. */
export const pauseTimelines = (kernel: KernelHandle, workspaceIds: readonly string[]): void => {
  for (const workspaceId of workspaceIds) {
    if (getWorkspaceIsTimelinePlaying(kernel, workspaceId)) setWorkspaceIsTimelinePlaying(kernel, workspaceId, false);
  }
};

export const selectWorkspaceIsTimelinePlaying = (
  state: KernelState,
  workspaceId: string
): boolean =>
  state.inventory.StudioWorkspace.registry[normalizeWorkspaceId(workspaceId)]?.isTimelinePlaying === true;

// --- timelinePlaybackRate ---------------------------------------------------

export const getWorkspaceTimelinePlaybackRate = (
  kernel: KernelHandle,
  workspaceId: string
): number => normalizeRate(readRuntime(kernel, workspaceId).timelinePlaybackRate);

export const setWorkspaceTimelinePlaybackRate = (
  kernel: KernelHandle,
  workspaceId: string,
  updater: RuntimeUpdater<number>
): void => {
  const normalizedId = normalizeWorkspaceId(workspaceId);
  if (!normalizedId) return;
  const next = normalizeRate(resolveUpdater(updater, getWorkspaceTimelinePlaybackRate(kernel, normalizedId)));
  kernel.patchEntityRuntimeState(WORKSPACE_ENTITY, normalizedId, { timelinePlaybackRate: next });
};

export const selectWorkspaceTimelinePlaybackRate = (
  state: KernelState,
  workspaceId: string
): number =>
  normalizeRate(
    state.inventory.StudioWorkspace.registry[normalizeWorkspaceId(workspaceId)]?.timelinePlaybackRate
  );

// --- isTimelineMuted --------------------------------------------------------

export const getWorkspaceIsTimelineMuted = (
  kernel: KernelHandle,
  workspaceId: string
): boolean => readRuntime(kernel, workspaceId).isTimelineMuted === true;

export const setWorkspaceIsTimelineMuted = (
  kernel: KernelHandle,
  workspaceId: string,
  updater: RuntimeUpdater<boolean>
): void => {
  const normalizedId = normalizeWorkspaceId(workspaceId);
  if (!normalizedId) return;
  const next = Boolean(resolveUpdater(updater, getWorkspaceIsTimelineMuted(kernel, normalizedId)));
  kernel.patchEntityRuntimeState(WORKSPACE_ENTITY, normalizedId, { isTimelineMuted: next });
};

export const selectWorkspaceIsTimelineMuted = (
  state: KernelState,
  workspaceId: string
): boolean =>
  state.inventory.StudioWorkspace.registry[normalizeWorkspaceId(workspaceId)]?.isTimelineMuted === true;

// --- isTimelineLoopEnabled --------------------------------------------------

const getWorkspaceIsTimelineLoopEnabled = (
  kernel: KernelHandle,
  workspaceId: string
): boolean => readRuntime(kernel, workspaceId).isTimelineLoopEnabled === true;

export const setWorkspaceIsTimelineLoopEnabled = (
  kernel: KernelHandle,
  workspaceId: string,
  updater: RuntimeUpdater<boolean>
): void => {
  const normalizedId = normalizeWorkspaceId(workspaceId);
  if (!normalizedId) return;
  const next = Boolean(resolveUpdater(updater, getWorkspaceIsTimelineLoopEnabled(kernel, normalizedId)));
  kernel.patchEntityRuntimeState(WORKSPACE_ENTITY, normalizedId, { isTimelineLoopEnabled: next });
};

export const selectWorkspaceIsTimelineLoopEnabled = (
  state: KernelState,
  workspaceId: string
): boolean =>
  state.inventory.StudioWorkspace.registry[normalizeWorkspaceId(workspaceId)]?.isTimelineLoopEnabled === true;
